import {
  AppPageShell,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ThemeSelector,
} from "@/components/ui";
import { Label } from "@/components/ui/shadcn/Label/Label";
import { UserProfilePhoto } from "@/components/ui/UserProfilePhoto/UserProfilePhoto";
import { useUser } from "@/components/UserProvider";
import {
  retrieveMatterSettingsQueryOptions,
  useUpdateUser,
  useUpdateUserProfilePicture,
} from "@/lib/data/react-query";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { omit } from "lodash";
import { Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type IntakeMethodFormValue = "role" | "team";

type ProfileSettingsForm = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  defaultIntakeMethod: IntakeMethodFormValue;
  defaultIntakeMethodId: string;
};

function buildInitialValues(
  currentUser: ReturnType<typeof useUser>["currentUser"],
): ProfileSettingsForm {
  return {
    firstName: currentUser.firstName,
    lastName: currentUser.lastName,
    email: currentUser.email,
    phoneNumber: currentUser.phoneNumber,
    defaultIntakeMethod:
      currentUser.defaultIntakeMethod === "team" ? "team" : "role",
    defaultIntakeMethodId: currentUser.defaultIntakeMethodId ?? "",
  };
}

export const ProfilePage = () => {
  const { currentUser, hasPagePermission } = useUser();
  const updateUser = useUpdateUser();
  const updateProfilePicture = useUpdateUserProfilePicture();
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const canAccessIntake = hasPagePermission(PAGE_PERMISSION_KEYS.intakeAccess);
  const { data: matterSettingsData, isLoading: isMatterSettingsLoading } =
    useQuery({
      ...retrieveMatterSettingsQueryOptions(),
      enabled: canAccessIntake,
    });
  const matterRolesSorted = useMemo(() => {
    const roles = matterSettingsData?.data.roles ?? [];
    return [...roles].sort((a, b) => a.label.localeCompare(b.label));
  }, [matterSettingsData?.data.roles]);
  const matterTeamsSorted = useMemo(() => {
    const teams = matterSettingsData?.data.teams ?? [];
    return [...teams].sort((a, b) => a.label.localeCompare(b.label));
  }, [matterSettingsData?.data.teams]);
  const canUpdateProfile = Boolean(true);

  const initialValues = useMemo(
    () => buildInitialValues(currentUser),
    [currentUser],
  );
  const [values, setValues] = useState<ProfileSettingsForm>(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initialValues),
    [initialValues, values],
  );

  const setValue = <TKey extends keyof ProfileSettingsForm>(
    key: TKey,
    value: ProfileSettingsForm[TKey],
  ) => {
    setValues((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleProfilePictureUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canUpdateProfile) {
      return;
    }

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsUploadingPicture(true);
    try {
      await updateProfilePicture.mutateAsync({
        user: currentUser,
        file,
      });
    } finally {
      setIsUploadingPicture(false);
      event.target.value = "";
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canUpdateProfile) {
      return;
    }

    await updateUser.mutateAsync({
      id: currentUser.id,
      dateModified: currentUser.dateModified,
      ...omit(values, ["email"]),
      defaultIntakeMethodId: values.defaultIntakeMethodId.trim() || undefined,
    });
  };

  return (
    <AppPageShell
      className="bg-background px-6 py-8 sm:px-10 sm:py-12"
      contentClassName="min-h-full w-full"
    >
      <form className="flex w-full flex-col" onSubmit={handleSubmit}>
        <div>
          <h1 className="text-foreground text-2xl font-semibold">
            User settings
          </h1>
        </div>

        <section className="mt-8">
          <h2 className="text-foreground text-base font-semibold">
            Profile information
          </h2>
          <div className="mt-5 flex items-center gap-4">
            <div className="relative shrink-0">
              <UserProfilePhoto user={currentUser} className="h-16 w-16" />
              <Input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                className="hidden"
                id="profile-picture-upload"
                disabled={isUploadingPicture || !canUpdateProfile}
              />
              <Label
                htmlFor="profile-picture-upload"
                className={cn(
                  "bg-card text-card-foreground absolute right-0 bottom-0 flex h-7 w-7 items-center justify-center rounded-full border shadow-sm",
                  canUpdateProfile && !isUploadingPicture
                    ? "hover:bg-accent hover:text-accent-foreground cursor-pointer"
                    : "cursor-not-allowed opacity-60",
                )}
              >
                <Upload className="h-3.5 w-3.5" />
                <span className="sr-only">Upload profile picture</span>
              </Label>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium">Profile picture</p>
              <p className="text-muted-foreground text-sm">
                {isUploadingPicture
                  ? "Uploading..."
                  : "Upload a photo used across LawWorks."}
              </p>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-x-4 gap-y-5 md:grid-cols-2">
            <ProfileTextField
              label="First name"
              value={values.firstName}
              disabled={!canUpdateProfile}
              onChange={(value) => {
                setValue("firstName", value);
              }}
            />
            <ProfileTextField
              label="Last name"
              value={values.lastName}
              disabled={!canUpdateProfile}
              onChange={(value) => {
                setValue("lastName", value);
              }}
            />
            <ProfileTextField
              label="Email"
              type="email"
              value={values.email}
              disabled
              onChange={(value) => {
                setValue("email", value);
              }}
            />
            <ProfileTextField
              label="Phone number"
              value={values.phoneNumber}
              disabled={!canUpdateProfile}
              onChange={(value) => {
                setValue("phoneNumber", value);
              }}
            />
          </div>
        </section>

        <SettingsDivider />

        <section>
          <h2 className="text-foreground text-base font-semibold">
            Appearance
          </h2>
          <div className="mt-5">
            <ThemeSelector />
          </div>
        </section>

        {canAccessIntake ? (
          <>
            <SettingsDivider />

            <section>
              <h2 className="text-foreground text-base font-semibold">
                Intakes preferences
              </h2>
              <div className="mt-5 flex max-w-xl flex-col gap-5">
                <div className="space-y-2">
                  <Label
                    className="text-sm font-medium"
                    htmlFor="intake-method"
                  >
                    Intake method
                  </Label>
                  <Select
                    value={values.defaultIntakeMethod}
                    onValueChange={(next) => {
                      setValue(
                        "defaultIntakeMethod",
                        next as IntakeMethodFormValue,
                      );
                      setValue("defaultIntakeMethodId", "");
                    }}
                    disabled={!canUpdateProfile}
                  >
                    <SelectTrigger id="intake-method" className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="role">Role</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {values.defaultIntakeMethod === "role" ? (
                  <div className="space-y-2">
                    <Label
                      className="text-sm font-medium"
                      htmlFor="intake-matter-role"
                    >
                      Matter role
                    </Label>
                    <Select
                      value={
                        matterRolesSorted.some(
                          (r) => r.id === values.defaultIntakeMethodId,
                        )
                          ? values.defaultIntakeMethodId
                          : undefined
                      }
                      onValueChange={(next) => {
                        setValue("defaultIntakeMethodId", next);
                      }}
                      disabled={!canUpdateProfile || isMatterSettingsLoading}
                    >
                      <SelectTrigger id="intake-matter-role" className="h-9">
                        <SelectValue
                          placeholder={
                            isMatterSettingsLoading
                              ? "Loading roles…"
                              : "Select a matter role"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {matterRolesSorted.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label
                      className="text-sm font-medium"
                      htmlFor="intake-matter-team"
                    >
                      Matter team
                    </Label>
                    <Select
                      value={
                        matterTeamsSorted.some(
                          (t) => t.id === values.defaultIntakeMethodId,
                        )
                          ? values.defaultIntakeMethodId
                          : undefined
                      }
                      onValueChange={(next) => {
                        setValue("defaultIntakeMethodId", next);
                      }}
                      disabled={!canUpdateProfile || isMatterSettingsLoading}
                    >
                      <SelectTrigger id="intake-matter-team" className="h-9">
                        <SelectValue
                          placeholder={
                            isMatterSettingsLoading
                              ? "Loading teams…"
                              : "Select a matter team"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {matterTeamsSorted.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </section>
          </>
        ) : null}

        <SettingsDivider />

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setValues(initialValues);
            }}
            disabled={!isDirty || updateUser.isPending}
          >
            Cancel
          </Button>
          {canUpdateProfile ? (
            <Button type="submit" disabled={!isDirty || updateUser.isPending}>
              {updateUser.isPending ? "Saving..." : "Save changes"}
            </Button>
          ) : null}
        </div>
      </form>
    </AppPageShell>
  );
};

function ProfileTextField({
  label,
  value,
  disabled,
  type = "text",
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  type?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <Input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="h-9"
      />
    </div>
  );
}

function SettingsDivider() {
  return <div className="border-border my-8 border-t" />;
}
